import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageX, Save } from "lucide-react";
import CadastroRapidoModal from "../../components/estoque/CadastroRapidoModal";
import PageContainer from "../../components/PageContainer";
import ProductAutocomplete from "../../components/ProductAutocomplete";
import { perdaAvariaService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";

const uuid = () =>
  crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

export default function PerdasAvarias() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const confirmar = useConfirmDialog();
  const [produtoId, setProdutoId] = useState("");
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [tipo, setTipo] = useState("PERDA");
  const [quantidade, setQuantidade] = useState(1);
  const [motivo, setMotivo] = useState("");
  const [cadastroRapido, setCadastroRapido] = useState(false);

  const dados = useQuery({
    queryKey: ["perdas-avarias"],
    queryFn: async () => ({
      produtos: await perdaAvariaService.produtos(""),
      historico: await perdaAvariaService.historico(),
    }),
    retry: 0,
  });
  const saldo = useQuery({
    queryKey: ["perdas-avarias", "saldo", produtoId],
    queryFn: () => perdaAvariaService.saldo(produtoId),
    enabled: Boolean(produtoId),
    retry: 0,
  });
  const registro = useMutation({
    mutationFn: (entrada) => perdaAvariaService.registrar(entrada),
    onSuccess: () => {
      showSuccess(
        `${tipo === "PERDA" ? "Perda" : "Avaria"} registrada com sucesso.`,
      );
      setProdutoId("");
      setProdutoSelecionado(null);
      setQuantidade(1);
      setMotivo("");
      queryClient.invalidateQueries({ queryKey: ["perdas-avarias"] });
    },
    onError: (erro) => showError(erro.message),
  });

  const produtos = dados.data?.produtos || [];
  const historico = dados.data?.historico || [];
  const opcoes = produtos.map((produto) => ({
    value: produto.produtoId,
    label: `${produto.codigo} — ${produto.descricao} (${produto.saldoAtual} ${
      produto.unidadeSimbolo || ""
    })`,
  }));

  const salvar = async (evento) => {
    evento.preventDefault();
    if (!produtoId || !saldo.data) return;
    if (!motivo.trim()) {
      showError("Informe o motivo da perda ou avaria.");
      return;
    }

    const nome = tipo === "PERDA" ? "perda" : "avaria";
    const confirmado = await confirmar({
      title: `Confirmar ${nome}`,
      description: `Registrar ${quantidade} ${
        saldo.data.unidadeSimbolo || "unidade(s)"
      } de ${saldo.data.descricao} como ${nome}? Essa saída reduz o estoque.`,
      variant: "warning",
    });
    if (!confirmado) return;

    registro.mutate({
      produtoId,
      tipo,
      quantidade: Number(quantidade),
      motivo,
      expectedVersion: saldo.data.versaoSaldo,
      requestId: uuid(),
    });
  };

  if (dados.isLoading) {
    return <PageContainer className="p-6">Carregando...</PageContainer>;
  }

  if (dados.isError) {
    return (
      <PageContainer className="p-6">
        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700">
          {dados.error.message}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Perdas e avarias</h1>
        <p className="text-gray-600">
          Registre produtos perdidos ou avariados. O saldo será reduzido e o
          motivo ficará no histórico.
        </p>
      </div>

      <form
        onSubmit={salvar}
        className="mb-8 rounded-lg border bg-white p-5 shadow-sm"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="md:col-span-2">
            Produto *
            <ProductAutocomplete
              value={produtoId}
              onChange={(id) => {
                setProdutoId(id);
                setProdutoSelecionado(null);
              }}
              selectedOption={produtoSelecionado}
              placeholder="Pesquise por código ou descrição"
              options={opcoes}
            />
            <button
              type="button"
              onClick={() => setCadastroRapido(true)}
              className="mt-2 rounded border border-blue-300 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
            >
              Novo produto
            </button>
          </label>

          <label>
            Tipo *
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="mt-1 w-full rounded border p-2"
            >
              <option value="PERDA">Perda</option>
              <option value="AVARIA">Avaria</option>
            </select>
          </label>

          <label>
            Quantidade *
            <input
              required
              type="number"
              min="1"
              max="2147483647"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              className="mt-1 w-full rounded border p-2"
            />
          </label>

          <label className="md:col-span-2">
            Motivo *
            <textarea
              required
              maxLength="1000"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Descreva o motivo da perda ou avaria"
              className="mt-1 min-h-24 w-full rounded border p-2"
            />
          </label>
        </div>

        {saldo.data && (
          <div className="mt-4 rounded bg-gray-50 p-3 text-sm">
            Saldo disponível:{" "}
            <b>
              {saldo.data.saldoAtual} {saldo.data.unidadeSimbolo}
            </b>
          </div>
        )}
        {saldo.isError && (
          <p className="mt-3 text-sm text-red-600">{saldo.error.message}</p>
        )}

        <button
          type="submit"
          disabled={
            registro.isPending ||
            !saldo.data ||
            Number(quantidade) > Number(saldo.data?.saldoAtual || 0) ||
            !motivo.trim()
          }
          className="mt-5 flex items-center gap-2 rounded bg-amber-600 px-4 py-2 text-white disabled:bg-gray-400"
        >
          <Save size={17} />
          {registro.isPending ? "Registrando…" : "Registrar perda ou avaria"}
        </button>
      </form>

      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
        <PackageX size={19} />
        Histórico de perdas e avarias
      </h2>
      <div className="overflow-x-auto rounded border bg-white">
        <table className="estoque-table table-perdas w-full min-w-[700px] text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="p-3">Data</th>
              <th>Produto</th>
              <th>Tipo</th>
              <th>Quantidade</th>
              <th>Saldo posterior</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {historico.map((item) => (
              <tr className="border-t" key={item.id}>
                <td className="p-3">{item.criadoEm || "—"}</td>
                <td>{item.produtoDescricao}</td>
                <td>
                  <span
                    className={`rounded px-2 py-1 ${
                      item.tipo === "PERDA"
                        ? "bg-rose-100 text-rose-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {item.tipo === "PERDA" ? "Perda" : "Avaria"}
                  </span>
                </td>
                <td>
                  {item.quantidade} {item.unidadeSimbolo}
                </td>
                <td>
                  {item.saldoPosterior} {item.unidadeSimbolo}
                </td>
                <td>{item.motivo}</td>
              </tr>
            ))}
            {!historico.length && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  Nenhuma perda ou avaria registrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CadastroRapidoModal
        open={cadastroRapido}
        tipo="produto"
        onClose={() => setCadastroRapido(false)}
        onCreated={(produto) => {
          setProdutoSelecionado({
            value: produto.id,
            label: `${produto.codigo || "Sem código"} — ${produto.descricao}`,
          });
          setProdutoId(produto.id);
          setCadastroRapido(false);
          queryClient.invalidateQueries({ queryKey: ["perdas-avarias"] });
        }}
      />
    </PageContainer>
  );
}
